import { useState } from 'react'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { Terminal, Play, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CliArg {
  name: string
  label: string
  required: boolean
  placeholder: string
}

interface CliCommand {
  id: string
  name: string
  description: string
  args: CliArg[]
  safe: boolean
}

interface RunResult {
  status: 'ok' | 'error'
  message?: string
  queued?: number
  updatedRoles?: number
  [key: string]: unknown
}

interface CommandCardProps {
  command: CliCommand
}

function CommandCard({ command }: CommandCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [argValues, setArgValues] = useState<Record<string, string>>({})
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<RunResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)

  const handleRun = async () => {
    if (!command.safe && !confirmed) {
      setConfirmed(true)
      return
    }
    setRunning(true)
    setResult(null)
    setError(null)
    setConfirmed(false)
    try {
      const res = await flaskApi.post<RunResult>(`/cli/run/${command.id}`, argValues)
      setResult(res.data)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { description?: string } } })?.response?.data?.description
        ?? 'Command failed'
      setError(msg)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="bento-card overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="w-9 h-9 rounded-xl bg-[#076698]/10 text-[#076698] dark:bg-sky-900/30 dark:text-sky-300 flex items-center justify-center shrink-0">
          <Terminal size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-extrabold text-slate-700 dark:text-slate-200">{command.name}</p>
            {!command.safe && (
              <Badge variant="destructive" className="text-[9px] px-1.5 py-0.5 uppercase tracking-wider">Caution</Badge>
            )}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">{command.description}</p>
        </div>
        {expanded ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-border/30 pt-4 space-y-4">
          {command.args.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {command.args.map((arg) => (
                <div key={arg.name} className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {arg.label}{arg.required && ' *'}
                  </label>
                  <Input
                    value={argValues[arg.name] ?? ''}
                    onChange={(e) => setArgValues((prev) => ({ ...prev, [arg.name]: e.target.value }))}
                    placeholder={arg.placeholder}
                    className="text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          {!command.safe && confirmed && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>This action cannot be undone. Click Run again to confirm.</span>
            </div>
          )}

          {result && (
            <div className={cn(
              'flex items-start gap-2 p-3 rounded-xl border text-xs',
              result.status === 'ok'
                ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
            )}>
              {result.status === 'ok' ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <XCircle size={14} className="shrink-0 mt-0.5" />}
              <div>
                <p className="font-bold">{result.status === 'ok' ? 'Success' : 'Failed'}</p>
                <p className="mt-0.5">{result.message ?? JSON.stringify(result)}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">
              <XCircle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button
            size="sm"
            className={cn(
              'gap-1.5 text-xs',
              confirmed ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#076698] hover:bg-[#055580]',
            )}
            onClick={handleRun}
            disabled={running}
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {confirmed ? 'Confirm Run' : 'Run'}
          </Button>
        </div>
      )}
    </div>
  )
}

export function CliOpsPage() {
  const { data: commands, isLoading, error } = useQuery<CliCommand[]>({
    queryKey: ['cli', 'commands'],
    queryFn: () => flaskApi.get<CliCommand[]>('/cli/commands').then((r) => r.data),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">CLI Operations</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Execute backend maintenance commands directly from the admin panel.
        </p>
      </div>

      <div className="bento-card p-5 flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Commands marked <span className="font-bold text-red-500">Caution</span> may have irreversible effects.
          Review arguments carefully before running.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" /> Loading commands...
        </div>
      )}

      {!!error && (
        <div className="bento-card p-5 text-sm text-red-500">Failed to load CLI commands.</div>
      )}

      {commands && commands.length > 0 && (
        <div className="space-y-3">
          {commands.map((cmd) => (
            <CommandCard key={cmd.id} command={cmd} />
          ))}
        </div>
      )}
    </div>
  )
}
